<script setup lang="ts">
import { useApiStore, useContextStore, usePeopleStore } from "../data/store.ts";
import { computed, ref } from "vue";
import FloorPlan from "./FloorPlan.vue";

const contextStore = useContextStore();
const apiStore = useApiStore();
const peopleStore = usePeopleStore();

const showFloorplan = ref(false);
const showPersonPicker = ref(false);
const personSearch = ref<string>("");

const filteredPeople = computed(() => {
  if (personSearch.value.length === 0) return peopleStore.sortedPeople;
  return peopleStore.sortedPeople.filter(
    (person) =>
      person.name.toLowerCase().indexOf(personSearch.value.toLowerCase()) >= 0,
  );
});

function pickPerson(uid: string) {
  contextStore.pickUser(uid);
  showPersonPicker.value = false;
  personSearch.value = "";
}
</script>

<template>
  <template v-if="peopleStore.people === null">
    <h1>Hi!</h1>
    <p class="lead">Please wait while data is loaded...</p>
  </template>

  <template v-else>
    <div v-if="contextStore.user !== null" class="mb-4 mt-5 row">
      <div v-if="apiStore.isOutdated" class="outdated-notice">
        Information might be outdated. Please reload!
      </div>
      <div class="col">
        <h1 class="d-flex flex-row justify-content-center">
          <span>Hi {{ peopleStore.people?.[contextStore.user]?.short }}!</span>
          <i
            style="font-size: 0.35em"
            class="bi-person-gear"
            role="button"
            @click="showPersonPicker = !showPersonPicker"
          ></i>
        </h1>
        <p class="lead">
          <slot />
        </p>
        <p>
          <slot name="icons" />
          <i
            class="bi-map icon-btn"
            role="button"
            @click="showFloorplan = !showFloorplan"
          />
        </p>
      </div>
    </div>

    <div
      v-if="showPersonPicker"
      class="card mb-4 p-3 pt-2 col-lg-6 col ms-auto me-auto text-start"
    >
      <i
        class="bi-x-lg text-muted fw-bold ms-auto"
        role="button"
        @click="showPersonPicker = false"
      ></i>
      <p class="lead">Please pick yourself from the list.</p>
      <div class="col-lg-6 col">
        <div class="input-group mb-2">
          <i
            class="bi-search border border-end-0 p-2 rounded-start text-muted"
          ></i>
          <input
            v-model="personSearch"
            class="form-control border-start-0 border"
            type="search"
            placeholder="search"
          />
        </div>
      </div>

      <div class="list-group overflow-auto" style="max-height: 15rem">
        <button
          v-for="person in filteredPeople"
          :key="person.id"
          class="list-group-item list-group-item-action"
          :class="{ active: contextStore.user === person.id }"
          type="button"
          @click="pickPerson(person.id)"
        >
          {{ person.short }}
          <span class="text-muted" style="font-size: 0.6em"
            >({{ person.name }})</span
          >
        </button>
      </div>
    </div>
  </template>

  <FloorPlan v-if="showFloorplan" class="mt-5" />
</template>

<style scoped></style>
